
export default function ServiceAreaMap() {
  const regions = [
    {
      icon: 'ri-building-2-line',
      title: 'Twin Cities Metro',
      description:
        'Full coverage across Minneapolis, St. Paul, Eagan, Bloomington, Burnsville, Lakeville, Apple Valley, and surrounding suburbs.',
      cities: [
        'Minneapolis',
        'St. Paul',
        'Eagan',
        'Bloomington',
        'Burnsville',
        'Lakeville',
        'Apple Valley',
        'Woodbury',
        'Plymouth',
        'Maple Grove',
      ],
    },
    {
      icon: 'ri-compass-3-line',
      title: 'Southeast Minnesota',
      description:
        'Serving Rochester, Owatonna, Faribault, Northfield, Red Wing, Winona, Austin, and the surrounding communities.',
      cities: [
        'Rochester',
        'Owatonna',
        'Faribault',
        'Northfield',
        'Red Wing',
        'Winona',
        'Austin',
        'Albert Lea',
        'Kasson',
        'Dodge Center',
      ],
    },
    {
      icon: 'ri-map-2-line',
      title: 'South Central Minnesota',
      description:
        'Based in Madison Lake with coverage to Mankato, St. Peter, Waseca, Le Sueur, New Ulm, and all nearby areas.',
      cities: [
        'Madison Lake',
        'Mankato',
        'St. Peter',
        'Waseca',
        'Le Sueur',
        'New Ulm',
        'Janesville',
        'Eagle Lake',
        'Lake Crystal',
        'Good Thunder',
      ],
    },
  ];

  return (
    <section className="py-24 bg-gray-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-orange-500/10 text-orange-500 font-semibold text-sm px-4 py-2 rounded-full mb-4">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-map-pin-line text-lg"></i>
            </div>
            Based in Madison Lake &amp; Eagan
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-orange-500">Service Area</span>
          </h2>
          <p className="text-lg text-gray-500 max-w-2xl mx-auto">
            We bring professional mobile welding and fabrication services across Southern and
            Central Minnesota — wherever the job takes us.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 items-start">
          {/* Map */}
          <div className="lg:col-span-3 rounded-lg overflow-hidden shadow-xl border border-gray-200">
            <div className="w-full h-[500px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d750000!2d-93.5!3d44.3!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Service Area Map - Southern Minnesota"
              ></iframe>
            </div>
            <div className="bg-gray-900 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-orange-500 rounded-full">
                  <i className="ri-truck-line text-lg text-white"></i>
                </div>
                <span className="text-white font-semibold text-sm">
                  Mobile Service — We Come to You
                </span>
              </div>
              <div className="flex items-center gap-4">
                <a
                  href="tel:+15073980496"
                  className="whitespace-nowrap flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors cursor-pointer text-sm font-semibold"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-line text-base"></i>
                  </div>
                  (507) 398-0496
                </a>
                <a
                  href="tel:+15072263462"
                  className="whitespace-nowrap flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors cursor-pointer text-sm font-semibold"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-phone-line text-base"></i>
                  </div>
                  (507) 226-3462
                </a>
              </div>
            </div>
          </div>

          {/* Region Cards */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            {regions.map((region, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 border border-gray-200 hover:border-orange-500 hover:shadow-lg transition-all duration-300 cursor-pointer group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-orange-500/10 group-hover:bg-orange-500 rounded-lg transition-colors duration-300 shrink-0">
                    <i
                      className={`${region.icon} text-2xl text-orange-500 group-hover:text-white transition-colors duration-300`}
                    ></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{region.title}</h3>
                    <p className="text-sm text-gray-500 mb-3 leading-relaxed">{region.description}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {region.cities.slice(0, 6).map((city, idx) => (
                        <span
                          key={idx}
                          className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-md"
                        >
                          {city}
                        </span>
                      ))}
                      {region.cities.length > 6 && (
                        <span className="text-xs bg-orange-50 text-orange-500 px-2 py-1 rounded-md font-semibold">
                          +{region.cities.length - 6} more
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* CTA Card */}
            <div className="bg-gray-900 rounded-lg p-6 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-orange-500 rounded-full mx-auto mb-3">
                <i className="ri-question-line text-2xl text-white"></i>
              </div>
              <h4 className="text-white font-bold mb-2">Not Sure If We Cover Your Area?</h4>
              <p className="text-gray-400 text-sm mb-4">
                Give us a call — we travel far and wide for the right job.
              </p>
              <a
                href="tel:+15073980496"
                className="whitespace-nowrap inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm px-5 py-3 rounded-lg transition-colors duration-300 cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line text-base"></i>
                </div>
                Call Us Today
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
